/**
 * Workspace service
 * Handles workspace-related API operations
 */

import { api } from '../utils/api-client'
import { logger } from '@/lib/logger'

/**
 * Workspace data interface
 */
export interface Workspace {
  id: string
  name: string
  slug?: string | null
  description?: string | null
  createdAt: string
  updatedAt?: string | null
}

/**
 * Workspace update data interface
 */
export interface UpdateWorkspaceData {
  name: string
  slug?: string | null
  description?: string | null
}

/**
 * Compute backend the Super Agent uses for shell + filesystem operations.
 * `daytona` provisions a per-session cloud sandbox; `local` writes to a
 * folder on the hosted server. Other backends supported by `@taqat/ai`
 * (docker, memory) are intentionally not surfaced in the UI today.
 */
export type SuperAgentComputeBackend = 'daytona' | 'local'

/**
 * Maximum length of the workspace-level additional instructions that get
 * appended to the Super Agent's prompt. Kept short — this is supplemental
 * guidance, not a replacement for the system prompt.
 */
export const SUPER_AGENT_ADDITIONAL_INSTRUCTIONS_MAX = 4_000

/**
 * Workspace-level Super-Agent settings. The full payload is normalised
 * client-side so older backends that only return `showFollowUp` still
 * deliver a complete shape to consumers.
 */
export interface SuperAgentSettings {
  showFollowUp: boolean
  showToolCalls: boolean
  showToolOutput: boolean
  modelId: string | null
  computeBackend: SuperAgentComputeBackend
  /** Whether the Super-Agent may use the Git capability (git CLI + GitHub PR review). Opt-in, off by default. */
  gitEnabled: boolean
  additionalInstructions: string
}

/** Safe defaults for a brand-new workspace. */
const SUPER_AGENT_DEFAULTS: SuperAgentSettings = {
  showFollowUp: false,
  showToolCalls: true,
  showToolOutput: true,
  modelId: null,
  computeBackend: 'daytona',
  gitEnabled: false,
  additionalInstructions: '',
}

function normalizeSuperAgentSettings(
  raw: Partial<SuperAgentSettings> | null | undefined,
): SuperAgentSettings {
  if (!raw) return { ...SUPER_AGENT_DEFAULTS }
  const computeBackend: SuperAgentComputeBackend =
    raw.computeBackend === 'local' ? 'local' : 'daytona'
  return {
    showFollowUp: raw.showFollowUp === true,
    // Default-on: only an explicit `false` hides tool activity, matching the
    // backend so older payloads (which omit these) keep full visibility.
    showToolCalls: raw.showToolCalls !== false,
    showToolOutput: raw.showToolOutput !== false,
    modelId: typeof raw.modelId === 'string' ? raw.modelId : null,
    computeBackend,
    gitEnabled: raw.gitEnabled === true,
    additionalInstructions:
      typeof raw.additionalInstructions === 'string'
        ? raw.additionalInstructions
        : '',
  }
}

/**
 * Workspace chat settings interface
 */
export interface WorkspaceChatSettings {
  model: string
  modelProvider: string
  instructions?: string
  reasoningEnabled?: boolean
  providerApiKey?: string
  providerBaseUrl?: string
}

/**
 * API response interfaces
 */
interface WorkspaceResponse {
  success: boolean
  data: Workspace
  message?: string
  error?: string
}

interface WorkspaceChatSettingsResponse {
  success: boolean
  data: {
    settings: WorkspaceChatSettings | null
  }
  message?: string
  error?: string
}

/**
 * Workspace service class
 */
export class WorkspaceService {
  /**
   * Gets the current workspace settings
   * @returns Promise resolving to workspace data
   */
  static async getWorkspace(): Promise<Workspace> {
    try {
      logger.debug('WorkspaceService - Making API call to /workspace');
      const response = await api.get('/workspace', {
        requiresAuth: true,
        showErrorToast: false, // We'll handle errors in the component
      }) as WorkspaceResponse
      
      if (!response.success) {
        throw new Error('Failed to fetch workspace data')
      }
      
      return response.data
    } catch (error) {
      logger.error('WorkspaceService - Error fetching workspace', error);
      // Re-throw with better error message
      if (error instanceof Error) {
        throw new Error(`Workspace fetch failed: ${error.message}`)
      }
      throw new Error('Failed to fetch workspace settings')
    }
  }
  
  /**
   * Updates the current workspace settings
   * @param updateData - Data to update the workspace with
   * @returns Promise resolving to updated workspace data
   */
  static async updateWorkspace(updateData: UpdateWorkspaceData): Promise<Workspace> {
    try {
      const response = await api.put('/workspace', updateData, {
        requiresAuth: true,
        showErrorToast: false, // We'll handle errors in the component
        showSuccessToast: false, // We'll handle success in the component
      }) as WorkspaceResponse
      
      if (!response.success) {
        throw new Error('Failed to update workspace')
      }
      
      return response.data
    } catch (error) {
      // Re-throw with better error message
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Failed to update workspace settings')
    }
  }

  /**
   * Gets the current workspace chat settings
   * @returns Promise resolving to chat settings response
   */
  static async getWorkspaceChatSettings(): Promise<WorkspaceChatSettingsResponse> {
    try {
      const response = await api.get('/workspace/chat-settings', {
        requiresAuth: true,
        showErrorToast: false, // We'll handle errors in the component
      }) as WorkspaceChatSettingsResponse
      
      return response
    } catch (error) {
      // Return error response format
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch chat settings'
      return {
        success: false,
        data: { settings: null },
        error: errorMessage
      }
    }
  }

  /**
   * Gets the workspace-level Super-Agent settings.
   * `modelId`, `computeBackend`, and `additionalInstructions` are extensions
   * to the original feature-flag payload — older backends that don't return
   * them fall through to safe defaults.
   * @returns Promise resolving to the full Super-Agent settings payload
   */
  static async getSuperAgentSettings(): Promise<SuperAgentSettings> {
    try {
      const response = (await api.get('/workspace/super-agent-settings', {
        requiresAuth: true,
        showErrorToast: false,
      })) as { success: boolean; data: { settings: Partial<SuperAgentSettings> } }
      return normalizeSuperAgentSettings(response?.data?.settings)
    } catch {
      return normalizeSuperAgentSettings(null)
    }
  }

  /**
   * Updates the workspace-level Super-Agent settings.
   * Accepts a partial payload — callers send only the fields they want to
   * change. The server merges on top of the persisted row.
   * @param updates - Partial settings updates
   * @returns Promise resolving to the persisted settings
   */
  static async updateSuperAgentSettings(
    updates: Partial<SuperAgentSettings>,
  ): Promise<SuperAgentSettings> {
    const response = (await api.put('/workspace/super-agent-settings', updates, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    })) as { success: boolean; data: { settings: Partial<SuperAgentSettings> } }
    return normalizeSuperAgentSettings(response?.data?.settings)
  }

  /**
   * Updates the current workspace chat settings
   * @param settings - Chat settings to update
   * @returns Promise resolving to updated chat settings response
   */
  static async updateWorkspaceChatSettings(settings: WorkspaceChatSettings): Promise<WorkspaceChatSettingsResponse> {
    try {
      const response = await api.put('/workspace/chat-settings', settings, {
        requiresAuth: true,
        showErrorToast: false, // We'll handle errors in the component
        showSuccessToast: false, // We'll handle success in the component
      }) as WorkspaceChatSettingsResponse
      
      return response
    } catch (error) {
      // Return error response format
      const errorMessage = error instanceof Error ? error.message : 'Failed to update chat settings'
      return {
        success: false,
        data: { settings: null },
        error: errorMessage
      }
    }
  }
}

/**
 * Convenience methods for workspace operations
 */
export const workspaceService = {
  getWorkspace: WorkspaceService.getWorkspace,
  updateWorkspace: WorkspaceService.updateWorkspace,
  getWorkspaceChatSettings: WorkspaceService.getWorkspaceChatSettings,
  updateWorkspaceChatSettings: WorkspaceService.updateWorkspaceChatSettings,
  getSuperAgentSettings: WorkspaceService.getSuperAgentSettings,
  updateSuperAgentSettings: WorkspaceService.updateSuperAgentSettings,
}

/**
 * Workspace service with additional utilities
 */
export const workspace = {
  ...workspaceService,
  
  /**
   * Validates workspace name
   */
  validateName(name: string): { isValid: boolean; error?: string } {
    if (!name || typeof name !== 'string') {
      return { isValid: false, error: 'Workspace name is required' }
    }
    
    const trimmedName = name.trim()
    
    if (trimmedName.length < 3) {
      return { isValid: false, error: 'Workspace name must be at least 3 characters' }
    }
    
    if (trimmedName.length > 100) {
      return { isValid: false, error: 'Workspace name must not exceed 100 characters' }
    }
    
    return { isValid: true }
  },
  
  /**
   * Validates workspace description
   */
  validateDescription(description?: string | null): { isValid: boolean; error?: string } {
    if (!description) {
      return { isValid: true } // Description is optional
    }
    
    if (typeof description !== 'string') {
      return { isValid: false, error: 'Description must be a string' }
    }
    
    if (description.trim().length > 500) {
      return { isValid: false, error: 'Description must not exceed 500 characters' }
    }
    
    return { isValid: true }
  },
  
  /**
   * Validates complete workspace data
   */
  validateWorkspaceData(data: UpdateWorkspaceData): { isValid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {}
    
    const nameValidation = workspace.validateName(data.name)
    if (!nameValidation.isValid) {
      errors.name = nameValidation.error!
    }
    
    const descriptionValidation = workspace.validateDescription(data.description)
    if (!descriptionValidation.isValid) {
      errors.description = descriptionValidation.error!
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
}